import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { loginUser, requestPasswordReset, resetPassword } from "../api/controller/login";
import { signUpUser } from "../api/controller/SignUp";
import { Eye, EyeOff } from "lucide-react";
import TWA from "../assets/SLU-TWA-Logo.jpg";
import { useFlashMessage } from "../components/FlashMessageProvider";
import { RegionsCardData } from "../api/controller/videoController/regions";

export default function Login() {
  const [isRegister, setIsRegister] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const dialogRef = useRef(null);
  const [passwordError, setPasswordError] = useState("");
  const [agreementError, setAgreementError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { showMessage } = useFlashMessage();
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1);
  const [forgotPasswordOtp, setForgotPasswordOtp] = useState("");
  const [forgotPasswordNewPassword, setForgotPasswordNewPassword] = useState("");
  const [forgotPasswordConfirmPassword, setForgotPasswordConfirmPassword] = useState("");
  const [isProcessingForgotPassword, setIsProcessingForgotPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const forgotDialogRef = useRef(null);
  const [regionOptions, setRegionOptions] = useState([]);
  const [isLoadingRegions, setIsLoadingRegions] = useState(false);
  const [regionsError, setRegionsError] = useState("");

  const [formData, setFormData] = useState({
    fullName: "",
    userName: "",
    dob: "",
    email: "",
    organization: "",
    profilePhoto: "",
    passwordConfirm: "",
    phoneNumber: "",
    jobTitle: "",
    regions: [],
  });

  const toggleMode = () => {
    setIsRegister((prev) => !prev);
    setPasswordError("");
    setIsModalOpen(false);
    setIsForgotPasswordOpen(false);
    setForgotPasswordEmail("");
    setForgotPasswordError("");
    setForgotPasswordStep(1);
    setForgotPasswordOtp("");
    setForgotPasswordNewPassword("");
    setForgotPasswordConfirmPassword("");
    setIsProcessingForgotPassword(false);
    setIsSubmitting(false);
  };
  const navigate = useNavigate();
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (isSubmitting) return;

    if (isRegister && !termsAccepted) {
      setAgreementError("You must agree to the Terms & Conditions before continuing.");
      return;
    }

    setAgreementError("");

    // Get form values from the DOM
    const fullNameInput = document.querySelector('input[name="fullName"]');
    const emailInput = document.querySelector('input[name="email"]');
    const passwordInput = document.querySelector('input[name="password"]');
    const passwordConfirmInput = document.querySelector('input[name="passwordConfirm"]');

    const fullName = isRegister && fullNameInput ? fullNameInput.value.trim() : null;
    const email = emailInput ? emailInput.value.trim() : "";
    const organization = isRegister ? (formData.organization || "").trim() : "";
    const phoneNumber = isRegister ? (formData.phoneNumber || "+1").trim() : "";
    const jobTitle = isRegister ? (formData.jobTitle || "").trim() : "";
    const selectedRegions = isRegister
      ? (Array.isArray(formData.regions) ? formData.regions.map((value) => value.toString()) : [])
      : [];
    const password = passwordInput ? passwordInput.value.trim() : "";
    const passwordConfirm = passwordConfirmInput ? passwordConfirmInput.value.trim() : "";

    if (isRegister && !fullName) {
      setPasswordError("Full Name is required.");
      return;
    }
    if (isRegister && !organization) {
      setPasswordError("Organization is required.");
      return;
    }
    if (isRegister && !jobTitle) {
      setPasswordError("Job title is required.");
      return;
    }
    if (isRegister && !phoneNumber) {
      setPasswordError("Phone number is required.");
      return;
    }
    let normalizedPhoneNumber = phoneNumber;
    if (isRegister) {
      const digitsOnlyPhone = phoneNumber.replace(/\D/g, "");
      const localDigits = digitsOnlyPhone.startsWith("1") ? digitsOnlyPhone.slice(1) : digitsOnlyPhone;

      if (localDigits.length !== 10) {
        setPasswordError("Enter a valid US phone number (10 digits, with optional +1 country code).");
        return;
      }

      normalizedPhoneNumber = `+1${localDigits}`;
    }
    if (!email) {
      setPasswordError("Email is required.");
      return;
    }

    // if (isRegister) {
    //   const normalizedEmail = email.toLowerCase();
    //   if (!/(\.edu|\.com)$/i.test(normalizedEmail)) {
    //     setPasswordError("Registration currently requires an email ending with .edu or .com.");
    //     return;
    //   }
    // }

    // Password validation for both password and passwordConfirm
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
    if (isRegister && (!passwordRegex.test(password) || !passwordRegex.test(passwordConfirm))) {
      setPasswordError(
        "Password and Password Confirm must each be at least 8 characters long, include 1 lowercase, 1 uppercase, 1 number, and 1 special character."
      );
      return;
    }

    if (isRegister && password !== passwordConfirm) {
      setPasswordError("Passwords do not match.");
      return;
    }

    const currentModeIsRegister = isRegister;
    setPasswordError("");
    setIsSubmitting(true);

    try {
      if (!currentModeIsRegister) {
        const data = await loginUser({
          userName: email,
          password,
        });

        if (data.token) {
          localStorage.setItem("authToken", data.token);
        }
        if (data.userType) {
          localStorage.setItem("userType", data.userType);
        }
        setPasswordError("");
        showMessage("Login successful! Redirecting to home...", { type: "success" });
        navigate("/video-platform/regions");
      } else {
        const userData = {
          fullName: fullName || "",
          userName: formData.userName,
          email,
          organization,
          jobTitle,
          phoneNumber: normalizedPhoneNumber,
          regions: selectedRegions,
          profilePhoto: formData.profilePhoto,
          password,
          passwordConfirm,
          termsAccepted,
        };

        const data = await signUpUser(userData);
        if (data.token) {
          localStorage.setItem("authToken", data.token);
        }
        if (data.userType) {
          localStorage.setItem("userType", data.userType);
        }
        showMessage("Registration successful!", { type: "success" });
        setIsRegister(false);
        navigate("/video-platform/nextsteps");
      }
    } catch (error) {
      if (!currentModeIsRegister) {
        console.error("Login failed:", error);
        setPasswordError("Login failed. Please check your credentials.");
        showMessage("Login failed. Please check your credentials.", { type: "error" });
      } else {
        const { data } = error?.response || {};
        console.error("Registration failed:", data?.error);
        setPasswordError(data?.error || "Registration failed. Please try again.");
        showMessage("Registration failed. Please try again.", { type: "error" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRegionToggle = (regionId) => {
    if (!regionId) return;
    setFormData((prev) => {
      const current = Array.isArray(prev.regions) ? prev.regions : [];
      const id = regionId.toString();
      const exists = current.includes(id);
      const next = exists ? current.filter((value) => value !== id) : [...current, id];
      return { ...prev, regions: next };
    });
  };

  useEffect(() => {
    let ignore = false;

    const fetchRegions = async () => {
      setIsLoadingRegions(true);
      setRegionsError("");
      try {
        const raw = await RegionsCardData();
        const normalized = (Array.isArray(raw) ? raw : [])
          .map((region) => {
            if (!region) return null;
            const id = region._id || region.id || region.cityId || region.city;
            if (!id) return null;
            const city = region.city || region.name || "";
            const state = region.state || region.region || "";
            const labelParts = [city, state].filter(Boolean);
            return {
              id: id.toString(),
              label: labelParts.length ? labelParts.join(", ") : id.toString(),
            };
          })
          .filter(Boolean);

        if (!ignore) {
          setRegionOptions(normalized);
          setFormData((prev) => {
            const current = Array.isArray(prev.regions) ? prev.regions : [];
            if (!current.length) return prev;
            const allowed = new Set(normalized.map((item) => item.id));
            const filtered = current.filter((value) => allowed.has(value));
            if (filtered.length === current.length) return prev;
            return { ...prev, regions: filtered };
          });
        }
      } catch (error) {
        if (!ignore) {
          console.error("Error fetching regions list:", error);
          setRegionsError("Unable to load regions right now. Please try again later.");
          setRegionOptions([]);
        }
      } finally {
        if (!ignore) setIsLoadingRegions(false);
      }
    };

    fetchRegions();

    return () => {
      ignore = true;
    };
  }, []);
  
  // useEffect(() => {
  //   const stored = typeof window !== "undefined" ? localStorage.getItem("termsAccepted") : null;
  //   if (stored === "true") setTermsAccepted(true);
  // }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (termsAccepted) localStorage.setItem("termsAccepted", "true");
    else localStorage.removeItem("termsAccepted");
  }, [termsAccepted]);

  useEffect(() => {
    setPasswordError("");
  }, [isRegister]);

  useEffect(() => {
    const dialogEl = dialogRef.current;
    if (!dialogEl) return;

    if (isRegister && isModalOpen) {
      if (!dialogEl.open) dialogEl.showModal();
    } else if (dialogEl.open) {
      dialogEl.close();
    }
  }, [isRegister, isModalOpen]);

  useEffect(() => {
    const dialogEl = forgotDialogRef.current;
    if (!dialogEl) return;

    if (isForgotPasswordOpen) {
      if (!dialogEl.open) dialogEl.showModal();
    } else if (dialogEl.open) {
      dialogEl.close();
    }
  }, [isForgotPasswordOpen]);

  const handleDialogClose = () => {
    setIsModalOpen(false);
  };

  const handleForgotDialogClose = () => {
    setIsForgotPasswordOpen(false);
    setForgotPasswordEmail("");
    setForgotPasswordError("");
    setForgotPasswordStep(1);
    setForgotPasswordOtp("");
    setForgotPasswordNewPassword("");
    setForgotPasswordConfirmPassword("");
    setIsProcessingForgotPassword(false);
  };

  const handleForgotPasswordSubmit = async (event) => {
    event.preventDefault();
    if (forgotPasswordStep === 1) {
      const email = forgotPasswordEmail.trim();
      if (!email) {
        setForgotPasswordError("Email is required.");
        return;
      }

      setForgotPasswordError("");
      setIsProcessingForgotPassword(true);
      try {
        const response = await requestPasswordReset(email);
        const message = response?.message || "If an account exists for that email, an OTP has been sent.";
        showMessage(message, { type: "success" });
        setForgotPasswordStep(2);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          const message = error.response.data?.error || error.response.data?.message || "Password reset failed. Please try again.";
          setForgotPasswordError(message);
        } else {
          setForgotPasswordError("Password reset failed. Please try again.");
        }
      } finally {
        setIsProcessingForgotPassword(false);
      }
    } else {
      const otp = forgotPasswordOtp.trim();
      const newPassword = forgotPasswordNewPassword.trim();
      const confirmPassword = forgotPasswordConfirmPassword.trim();
      const email = forgotPasswordEmail.trim();

      if (!otp) {
        setForgotPasswordError("OTP is required.");
        return;
      }

      if (!newPassword || !confirmPassword) {
        setForgotPasswordError("Both password fields are required.");
        return;
      }

      if (newPassword !== confirmPassword) {
        setForgotPasswordError("Passwords do not match.");
        return;
      }

      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
      if (!passwordRegex.test(newPassword)) {
        setForgotPasswordError("Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.");
        return;
      }

      setForgotPasswordError("");
      setIsProcessingForgotPassword(true);
      try {
        const payload = {
          email,
          otp,
          newPassword,
          confirmPassword,
        };
        const response = await resetPassword(payload);
        const message = response?.message || "Password reset successfully. You can now sign in with your new password.";
        showMessage(message, { type: "success" });
        handleForgotDialogClose();
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          const message = error.response.data?.error || error.response.data?.message || "Password reset failed. Please try again.";
          setForgotPasswordError(message);
        } else {
          setForgotPasswordError("Password reset failed. Please try again.");
        }
      } finally {
        setIsProcessingForgotPassword(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#3D6E9B] to-[#003DA5] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={TWA} alt="SLU TWA Logo" className="h-12 mx-auto" />
          {/* <h1 className="font-['corporate-s'] text-[#003DA5] text-[28px] font-bold mb-2">
            Transformative Workforce Academy
          </h1> */}
          <p className="font-['corporate-s'] text-[#2b2b2b] font-semibold text-xl sm:text-2xl md:text-3xl">
            {isRegister ? "Create your account" : "Welcome back"}
          </p>
        </div>

        <div className="flex flex-col w-full font-['corporate-s']">
          {isRegister && (
            <>
              <label className="text-[14px] mb-1 text-[#2b2b2b]">Full Name</label>
              <input
                name="fullName"
                type="text"
                placeholder="Enter your full name"
                required
                className="mb-4 w-full h-[35px] text-[12px] px-4 rounded-[5px] border border-gray-300 bg-white text-black placeholder-black/60 font-['corporate-s'] focus:outline-none focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
                value={formData.fullName}
                onChange={handleChange}
              />

              <label className="text-[14px] mb-1 text-[#2b2b2b]">Organization</label>
              <input
                name="organization"
                type="text"
                placeholder="Enter your organization"
                required
                className="mb-4 w-full h-[35px] text-[12px] px-4 rounded-[5px] border border-gray-300 bg-white text-black placeholder-black/60 font-['corporate-s'] focus:outline-none focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
                value={formData.organization}
                onChange={handleChange}
              />

              <label className="text-[14px] mb-1 text-[#2b2b2b]">Job Title</label>
              <input
                name="jobTitle"
                type="text"
                placeholder="Enter your job title"
                required
                className="mb-4 w-full h-[35px] text-[12px] px-4 rounded-[5px] border border-gray-300 bg-white text-black placeholder-black/60 font-['corporate-s'] focus:outline-none focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
                value={formData.jobTitle}
                onChange={handleChange}
              />

              <label className="text-[14px] mb-1 text-[#2b2b2b]">Phone Number</label>
              <input
                name="phoneNumber"
                type="tel"
                inputMode="tel"
                placeholder="Enter your phone number"
                required
                className="mb-4 w-full h-[35px] text-[12px] px-4 rounded-[5px] border border-gray-300 bg-white text-black placeholder-black/60 font-['corporate-s'] focus:outline-none focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
                value={formData.phoneNumber}
                onChange={handleChange}
              />

              <div className="mb-4">
                <label className="block text-[14px] mb-1 text-[#2b2b2b]">Preferred Regions (select one or more)</label>
                <div className="border border-gray-300 rounded-[5px] bg-white p-3 max-h-40 overflow-y-auto">
                  {isLoadingRegions ? (
                    <p className="text-[12px] text-gray-500">Loading regions...</p>
                  ) : regionOptions.length ? (
                    regionOptions.map((option) => (
                      <label key={option.id} className="flex items-center gap-2 text-[12px] text-[#2b2b2b] mb-2 last:mb-0">
                        <input
                          type="checkbox"
                          value={option.id}
                          checked={Array.isArray(formData.regions) && formData.regions.includes(option.id)}
                          onChange={() => handleRegionToggle(option.id)}
                          className="h-4 w-4 text-[#003DA5]"
                        />
                        <span>{option.label}</span>
                      </label>
                    ))
                  ) : (
                    <p className="text-[12px] text-gray-500">No regions available.</p>
                  )}
                </div>
                {regionsError ? (
                  <p className="mt-1 text-[12px] text-red-500">{regionsError}</p>
                ) : (
                  <p className="mt-1 text-[12px] text-gray-500">You can select multiple regions to tailor your access.</p>
                )}
              </div>

              {/* <label className="text-[14px] mb-1 text-gray-700">Date of Birth</label>
              <input
                name="dob"
                type="date"
                required
                className="mb-4 w-full h-[35px] text-[12px] px-4 rounded-[5px] border border-gray-300 bg-white text-black placeholder-black/60 font-['corporate-s'] focus:outline-none focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
                value={formData.dob}
                onChange={handleChange}
              /> */}

              <label className="text-[14px] mb-1 text-[#2b2b2b]">Profile Photo</label>
              <input
                name="profilePhoto"
                required
                type="file"
                accept="image/*"
                className="mb-4 w-full h-[35px] text-[12px] px-4 rounded-[5px] border border-[#2b2b2b] bg-white text-black placeholder-black/60 font-['corporate-s'] focus:outline-none focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    profilePhoto: e.target.files[0],
                  }))
                }
              />

              <label className="text-[14px] mb-1 text-[#2b2b2b]">Username</label>
              <input
                name="userName"
                type="text"
                placeholder="Enter your username"
                required
                className="mb-4 w-full h-[35px] text-[12px] px-4 rounded-[5px] border border-gray-300 bg-white text-black placeholder-black/60 font-['corporate-s'] focus:outline-none focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
                value={formData.userName}
                onChange={handleChange}
              />
            </>
          )}

          

          <label className="text-[14px] mb-1 text-[#2b2b2b]">Email</label>
          <input
            name="email"
            type="email"
            placeholder="Enter your email address"
            required
            className="mb-4 w-full h-[35px] text-[12px] px-4 rounded-[5px] border border-gray-300 bg-white text-black placeholder-black/60 font-['corporate-s'] focus:outline-none focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
            value={formData.email}
            onChange={handleChange}
          />

          <label className="text-[14px] mb-1 text-[#2b2b2b]">Password</label>
          <div className="relative mb-2">
            <input
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              required
              className="w-full h-[35px] text-[12px] px-4 pr-16 rounded-[5px] border border-gray-300 bg-white text-black placeholder-black/60 font-['corporate-s'] focus:outline-none focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-3 flex items-center text-[#003DA5] hover:text-[#3D6E9B]"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {isRegister && (
            <>
              <label className="text-[14px] mb-1 text-[#2b2b2b]">Password Confirm</label>
              <div className="relative mb-4">
                <input
                  name="passwordConfirm"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Re-enter your password"
                  value={formData.passwordConfirm}
                  required
                  className="w-full h-[35px] text-[12px] px-4 pr-16 rounded-[5px] border border-gray-300 bg-white text-black placeholder-black/60 font-['corporate-s'] focus:outline-none focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-3 flex items-center text-[#003DA5] hover:text-[#3D6E9B]"
                  aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </>
          )}
          {/* Password error */}
          {passwordError && (
            <p className="text-red-500 text-[12px] mb-2 font-['corporate-s']">
              {passwordError}
            </p>
          )}

          {!isRegister && (
            <div className="flex items-center justify-between mb-4 text-[14px]">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" className="w-[15px] h-[15px]" />
                <span className="font-['corporate-s'] text-[#2b2b2b]">Remember me</span>
              </label>
              <button
                type="button"
                className="no-underline font-['corporate-s'] text-[#003DA5] hover:text-[#3D6E9B] cursor-pointer bg-transparent border-none p-0"
                onClick={() => {
                  setForgotPasswordError("");
                  setForgotPasswordStep(1);
                  setForgotPasswordOtp("");
                  setForgotPasswordNewPassword("");
                  setForgotPasswordConfirmPassword("");
                  setForgotPasswordEmail(formData.email || "");
                  setIsForgotPasswordOpen(true);
                }}
              >
                Forgot password?
              </button>
            </div>
          )}

{/* Modal */}
{isRegister && (
  <dialog
    ref={dialogRef}
    className="backdrop:bg-black/40 rounded-xl border-none p-0 max-w-4xl w-[90vw] m-auto"
    onClose={handleDialogClose}
    onCancel={(event) => {
      event.preventDefault();
      setIsModalOpen(false);
    }}
  >
    <div className="relative bg-[#f0f0f0] rounded-xl p-8 shadow-2xl max-h-[80vh] overflow-y-auto">
      <button
        onClick={() => {
          dialogRef.current?.close();
          setIsModalOpen(false);
        }}
        className="absolute top-4 right-4 text-[#2b2b2b] hover:text-gray-800 text-2xl font-bold"
        aria-label="Close modal"
        type="button"
      >
        ✕
      </button>

      <h3 className="text-3xl font-bold mb-6 text-[#003DA5] font-['farnham-display'] pr-10">
        Terms & Conditions
      </h3>

      {/* Scrollable Terms Content */}
      <div className="text-[#2b2b2b] text-sm leading-relaxed space-y-6 max-h-[60vh] overflow-y-auto pr-2">
        <h4 className="text-xl font-semibold text-[#00244D]">Faces of Talent: Video Resume Portal — Employer Terms and Conditions</h4>
        
        <p>
          Welcome to the <strong>Faces of Talent: Video Resume Portal (VRP)</strong>, created by Saint Louis University’s Transformative Workforce Academy (SLU TWA).<br />
          Faces of Talent connects employers with qualified jobseekers who have past justice involvement. The goal is to support fair chance hiring and expand access to meaningful employment opportunities. Before you view any video resumes, please read and agree to the following terms. These help protect the jobseekers featured here and ensure the portal is used responsibly.
        </p>

        <h5 className="text-lg font-semibold text-[#00244D]">Confidentiality and Privacy</h5>
        <ul className="list-disc pl-6 space-y-2">
          <li>The video resumes you view contain personal information.</li>
          <li>You agree to keep all information shared through this portal private and confidential, sharing only what is needed for hiring purposes.</li>
          <li>You may not share, record, or distribute any videos or information from the portal without written permission from SLU TWA.</li>
        </ul>

        <h5 className="text-lg font-semibold text-[#00244D]">Fair Hiring Commitment</h5>
        <p>By using this portal, you agree to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Evaluate candidates based on their skills, experience, and potential—not solely on past justice involvement.</li>
          <li>Follow all applicable federal, state, and local employment laws, including anti-discrimination and fair chance hiring laws.</li>
        </ul>

        <h5 className="text-lg font-semibold text-[#00244D]">Appropriate Use</h5>
        <p>
          You agree to use this portal only for legitimate recruiting and hiring purposes. Any misuse—such as using candidate information for marketing, solicitation, or any unrelated purpose—is strictly prohibited.
        </p>

        <h5 className="text-lg font-semibold text-[#00244D]">Accuracy and Responsibility</h5>
        <p>
          While SLU TWA takes care to share accurate information, we cannot guarantee that all content is complete or current. Employers are responsible for conducting their own due diligence and background checks in compliance with the law.
        </p>

        <h5 className="text-lg font-semibold text-[#00244D]">Agreement</h5>
        <p>By clicking “I Agree” or otherwise accessing the Faces of Talent portal, you confirm that:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>You have read and understood these Terms and Conditions.</li>
          <li>You agree to follow them in full.</li>
          <li>You understand that SLU TWA may revoke access if these terms are violated.</li>
        </ul>
      </div>

      {/* Agreement Checkbox/Text */}
      {/* <div className="mt-8 mb-6 text-center leading-tight">
        <label className="inline-flex items-center gap-3 text-md font-medium text-[#2b2b2b]">
          
          <span className="leading-snug">
            I Agree To The <span className="text-2xl font-black text-[#003DA5] leading-tight">TERMS AND CONDITIONS</span> And I Have Read The Privacy Notice.
          </span>
        </label>
      </div> */}
    </div>
  </dialog>
)}

<dialog
  ref={forgotDialogRef}
  className="backdrop:bg-black/40 rounded-xl border-none p-0 max-w-md w-[90vw] m-auto"
  onClose={handleForgotDialogClose}
  onCancel={(event) => {
    event.preventDefault();
    handleForgotDialogClose();
  }}
>
  <div className="relative bg-[#f0f0f0] rounded-xl p-6 shadow-2xl w-full max-h-[80vh] overflow-y-auto">
    <button
      onClick={() => {
        handleForgotDialogClose();
        forgotDialogRef.current?.close();
      }}
      className="absolute top-4 right-4 text-[#2b2b2b] hover:text-gray-800 text-2xl font-bold"
      aria-label="Close modal"
      type="button"
    >
      ✕
    </button>

    <h3 className="text-2xl font-bold mb-3 text-[#003DA5] font-['farnham-display'] pr-6">
      Reset Password
    </h3>
    <p className="text-sm text-[#2b2b2b] font-['corporate-s']">
      {forgotPasswordStep === 1
        ? "Enter the email associated with your account. If it exists in our system, we will send an OTP to continue."
        : "Enter the OTP sent to your email and choose a new password."}
    </p>

    <form onSubmit={handleForgotPasswordSubmit} className="mt-6 space-y-4">
      <div>
        <label className="block text-sm font-medium text-[#2b2b2b] mb-1" htmlFor="forgot-password-email">
          Email Address
        </label>
        <input
          id="forgot-password-email"
          type="email"
          value={forgotPasswordEmail}
          onChange={(event) => setForgotPasswordEmail(event.target.value)}
          disabled={forgotPasswordStep === 2}
          className="w-full h-[38px] text-sm px-4 rounded-[5px] border border-gray-300 bg-white text-black placeholder-black/60 font-['corporate-s'] focus:outline-none focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
          placeholder="you@example.com"
          required
        />
      </div>
      {forgotPasswordStep === 2 && (
        <>
          <div>
            <label className="block text-sm font-medium text-[#2b2b2b] mb-1" htmlFor="forgot-password-otp">
              OTP Code
            </label>
            <input
              id="forgot-password-otp"
              type="text"
              value={forgotPasswordOtp}
              onChange={(event) => setForgotPasswordOtp(event.target.value)}
              className="w-full h-[38px] text-sm px-4 rounded-[5px] border border-gray-300 bg-white text-black placeholder-black/60 font-['corporate-s'] focus:outline-none focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
              placeholder="Enter the OTP"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#2b2b2b] mb-1" htmlFor="forgot-password-new">
              New Password
            </label>
            <input
              id="forgot-password-new"
              type="password"
              value={forgotPasswordNewPassword}
              onChange={(event) => setForgotPasswordNewPassword(event.target.value)}
              className="w-full h-[38px] text-sm px-4 rounded-[5px] border border-gray-300 bg-white text-black placeholder-black/60 font-['corporate-s'] focus:outline-none focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
              placeholder="Enter new password"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#2b2b2b] mb-1" htmlFor="forgot-password-confirm">
              Confirm New Password
            </label>
            <input
              id="forgot-password-confirm"
              type="password"
              value={forgotPasswordConfirmPassword}
              onChange={(event) => setForgotPasswordConfirmPassword(event.target.value)}
              className="w-full h-[38px] text-sm px-4 rounded-[5px] border border-gray-300 bg-white text-black placeholder-black/60 font-['corporate-s'] focus:outline-none focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
              placeholder="Confirm new password"
              required
            />
          </div>
        </>
      )}
      {forgotPasswordError && (
        <p className="text-red-500 text-xs font-['corporate-s']">{forgotPasswordError}</p>
      )}
      <button
        type="submit"
        disabled={isProcessingForgotPassword}
        className={`w-full h-[38px] rounded-[5px] text-sm font-['corporate-s'] transition-colors duration-200 ${
          isProcessingForgotPassword ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-[#003DA5] hover:bg-[#3D6E9B] text-white cursor-pointer"
        }`}
      >
        {isProcessingForgotPassword
          ? forgotPasswordStep === 1
            ? "Sending OTP..."
            : "Resetting password..."
          : forgotPasswordStep === 1
            ? "Send OTP"
            : "Reset Password"}
      </button>
    </form>
  </div>
</dialog>

{/* Trigger Button (outside the modal) */}
{isRegister && (
  <div className="flex items-start gap-3 mb-4 text-[14px]">
    <input
      id="termsAccepted"
      type="checkbox"
      checked={termsAccepted}
      onChange={(event) => {
        const next = event.target.checked;
        setTermsAccepted(next);
        if (next) setAgreementError("");
      }}
      className="mt-1 h-4 w-4 text-[#003DA5] focus:ring-[#003DA5]"
    />
    <div className="text-[#2b2b2b] leading-snug">
      <label htmlFor="termsAccepted" className="cursor-pointer select-none">
        I agree to the 
      </label>{" "}
      <button
        type="button"
        className="ml-1 no-underline font-['corporate-s'] text-[#003DA5] hover:text-[#3D6E9B] cursor-pointer bg-transparent border-none p-0"
        onClick={() => setIsModalOpen(true)}
      >
        Terms & Conditions.
      </button>
    </div>
  </div>
)}

{agreementError && (
  <p className="text-red-500 text-[12px] mb-2 font-['corporate-s']">
    {agreementError}
  </p>
)}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={(isRegister && !termsAccepted) || isSubmitting}
            className={`w-full h-[35px] rounded-[5px] text-[14px] mb-4 font-['corporate-s'] transition-colors duration-200 ${
              (isRegister && !termsAccepted) || isSubmitting
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#003DA5] hover:bg-[#3D6E9B] text-white cursor-pointer"
            }`}
          >
            {isSubmitting
              ? isRegister
                ? "Registering..."
                : "Signing In..."
              : isRegister
                ? "Register"
                : "Sign In"}
          </button>

          <p
            className="text-[14px] mb-4 font-['corporate-s'] cursor-pointer text-center text-[#003DA5] hover:text-[#3D6E9B] transition-colors duration-200"
            onClick={toggleMode}
          >
            {isRegister ? "Already have an account? Sign In" : "New user? Register here"}
          </p>
        </div>

        {/* <div className="flex items-center my-4">
          <div className="flex-grow border-t border-gray-300"></div>
          <span className="mx-4 text-gray-500 font-['corporate-s']">OR</span>
          <div className="flex-grow border-t border-gray-300"></div>
        </div> */}

        {/* <button
          type="button"
         onClick={() => navigate("/")}
          className="w-full h-[35px] rounded-[5px] bg-gray-100 hover:bg-gray-200 text-gray-700 text-[14px] mb-4 font-['corporate-s'] cursor-pointer transition-colors duration-200"
        >
          Continue as a Guest
        </button> */}

        <p className="text-[12px] font-['corporate-s'] text-[#2b2b2b] text-center">
          Contact 314-977-4000 for help
        </p>
      </div>
    </div>
  );
}